import { useEffect, useState } from "react";
import { X, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";
import { eur, num, dateIt } from "@/lib/format";

/** Elenco delle pratiche approvate sul CRM senza data di liquidazione, esportabile in Excel. */
export function ApprovatoDaLiquidareModal({ anno, agenzia, risorsa, onClose }) {
  const [dati, setDati] = useState(null);
  const qs = `anno=${anno}${agenzia ? `&agenzia=${encodeURIComponent(agenzia)}` : ""}${
    risorsa ? `&risorsa=${encodeURIComponent(risorsa)}` : ""}`;

  useEffect(() => {
    api.get(`/corto/approvato-da-liquidare?${qs}`).then((r) => setDati(r.data)).catch(() => setDati({ righe: [] }));
  }, [qs]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1300] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose} data-testid="approvato-da-liquidare-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mt-8 mb-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 p-6 border-b border-slate-100">
          <div>
            <div className="uppercase text-xs tracking-[0.18em] font-semibold text-orange-600">Approvato da liquidare</div>
            <h2 className="text-lg font-semibold text-slate-900 mt-1">
              {agenzia || "Rete DBE"}{risorsa ? ` · ${risorsa}` : ""} · {anno}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Pratiche in stato approvato sul CRM Databeasy senza data di liquidazione: da verificare e correggere sul CRM.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/api/corto/approvato-da-liquidare.xlsx?${qs}&t=${Date.now()}`} data-testid="approvato-da-liquidare-excel"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-orange-600 text-white text-sm
                hover:bg-orange-700 transition-colors duration-200">
              <FileSpreadsheet size={16} /> Scarica Excel
            </a>
            <button onClick={onClose} data-testid="approvato-da-liquidare-chiudi"
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors duration-200">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-6">
          {!dati ? (
            <div className="text-sm text-slate-500">Caricamento…</div>
          ) : !dati.righe.length ? (
            <div className="text-sm text-slate-500" data-testid="approvato-da-liquidare-vuoto">Nessuna pratica approvata in attesa di liquidazione.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="approvato-da-liquidare-tabella">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Numero</th>
                    <th className="py-2 pr-3">Cliente</th>
                    {!agenzia && <th className="py-2 pr-3">Agenzia</th>}
                    <th className="py-2 pr-3">Prodotto</th>
                    <th className="py-2 pr-3">Caricamento</th>
                    <th className="py-2 pr-3">Approvazione</th>
                    <th className="py-2 pl-3 text-right">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {dati.righe.map((r) => (
                    <tr key={r.numero} className="border-b border-slate-100 hover:bg-orange-50/40 transition-colors duration-150">
                      <td className="py-2 pr-3 font-mono">{r.numero}</td>
                      <td className="py-2 pr-3 font-medium text-slate-900">{r.cliente}</td>
                      {!agenzia && <td className="py-2 pr-3">{r.agenzia}</td>}
                      <td className="py-2 pr-3 text-slate-600">{r.prodotto}</td>
                      <td className="py-2 pr-3">{dateIt(r.data_caricamento)}</td>
                      <td className="py-2 pr-3">{r.data_approvazione ? dateIt(r.data_approvazione) : "—"}</td>
                      <td className="py-2 pl-3 text-right font-mono">{eur(r.importo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold text-slate-900">
                    <td className="py-3 pr-3" colSpan={agenzia ? 5 : 6}>{num(dati.pratiche)} pratiche</td>
                    <td className="py-3 pl-3 text-right font-mono">{eur(dati.totale)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
